/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { TranslationStatusType } from 'src/translation/stores/TranslationJobStore';

type TranslationDocumentStoreData = OmitFunctions<Partial<Omit<TranslationDocumentStore, 'id'>>>;

export class TranslationDocumentStore {
    /* Document ID.  */
    public id: string;

    /* Location of the document or folder.  */
    public path: string;

    /* Location of the source document.  */
    public sourcePath: string;

    /* To language.  */
    public targetLanguageCode: string;

    /* Source language.  */
    public sourceLanguageCode: string;

    /*  Characters charged by the API. */
    public characterCharged: number;

    /* Operation created date time.  */
    public createdDateTime: Date;

    /* Date time in which the operation's status has been updated. */
    @observable public lastActionDateTime: Date;

    /* Status for job or document. */
    @observable public status: TranslationStatusType;

    /* Progress of the translation if available. */
    @observable public progress: number;

    @observable public error: string;

    constructor(id: string, data: TranslationDocumentStoreData) {
        this.id = id;
        this.path = data?.path ?? '';
        this.sourcePath = data?.sourcePath ?? '';
        this.createdDateTime = data?.createdDateTime ?? new Date();
        this.lastActionDateTime = data?.lastActionDateTime ?? new Date();
        this.error = data?.error ?? null;
        this.targetLanguageCode = data?.targetLanguageCode || null;
        this.sourceLanguageCode = data?.sourceLanguageCode || null;
        this.status = data?.status ?? 'NotStarted';
        this.progress = data?.progress ?? 0;
        this.characterCharged = data?.characterCharged;
    }

    @computed
    public get fileName() {
        return this.sourcePath
            .split('/')
            .pop()
            .replace(/%20/g, ' ');
    }

    @computed
    public get destinationFileName() {
        return this.path
            .split('/')
            .pop()
            .replace(/%20/g, ' ');
    }

    @computed
    public get destinationContainerName() {
        return this.path.split('/')[3];
    }

    @computed
    public get sourceContainerName() {
        return this.sourcePath.split('/')[3];
    }

    @computed
    public get destinationStorageAccountName() {
        const storageAccUrl = this.path.split('/')[2];
        return storageAccUrl.slice(0, storageAccUrl?.indexOf('.'));
    }

    @computed
    public get sourceStorageAccountName() {
        const storageAccUrl = this.sourcePath.split('/')[2];
        return storageAccUrl.slice(0, storageAccUrl.indexOf('.'));
    }

    @computed
    public get sourceFolderName() {
        const tokens = this.sourcePath.split('/');
        if (tokens.length > 5) {
            return tokens.slice(4, tokens.length - 1).join('/');
        }
        return null;
    }

    @computed
    public get destinationFolderName() {
        const tokens = this.path.split('/');
        if (tokens.length > 5) {
            return tokens.slice(4, tokens.length - 1).join('/');
        }
        return null;
    }

    @action
    public updateData(data: TranslationDocumentStoreData) {
        this.status = data.status;
        this.lastActionDateTime = data.lastActionDateTime;
        this.error = data.error;
        this.progress = data.progress;
    }

    @action
    public setLastActionDateTime(dateTime: Date) {
        this.lastActionDateTime = dateTime;
    }

    @action
    public setStatus(status: TranslationStatusType) {
        this.status = status;
    }

    @action
    public setPercentComplete(percentage: number) {
        this.progress = percentage;
    }
}
